<template>
    <div class="pizarra">
        <div class="chart-wrapper">
            <h1>Estado de Ánimo Empleados</h1>
            
            <!-- Mostrar gráfica solo cuando hay datos -->
            <div v-if="hasData" class="chart-container col-md-9 col-sm-12">
                <pie-chart 
                    :chart-data="chartData" 
                    :options="chartOptions"
                    :height="200"
                />
                <p class="data-info">Total de respuestas: {{ totalResponses }}</p>
            </div>
            
            <!-- Mostrar mensaje si no hay datos -->
            <div v-else class="no-data">
                <p>No hay datos disponibles para mostrar</p>
            </div>
        </div>
    </div>
</template>
  
<script>
import { Pie } from 'vue-chartjs';

// Componente de gráfica pie personalizado
const PieChart = {
  extends: Pie,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartData, this.options)
      }
    }
  }
}

import {
    HAPPINESS_M,
    GET_RESULTS_HAPPINESS,
    ACTION_GET_RESULTS_HAPPINESS,
} from "@/views/chart/store/types/types.js";
import { mapActions, mapGetters } from "vuex";
// Plugin refactorizado para texto centrado
const centerTextPlugin = {
    beforeDraw: function(chart) {
        if (!chart.config.options.elements.center) return;

        const ctx = chart.chart.ctx;
        const centerConfig = chart.config.options.elements.center;
        
        const textRenderer = new CenterTextRenderer(ctx, centerConfig, chart);
        textRenderer.render();
    }
};

// Clase para manejar el renderizado de texto centrado
class CenterTextRenderer {
    constructor(ctx, config, chart) {
        this.ctx = ctx;
        this.config = this.extractConfig(config);
        this.chart = chart;
        this.dimensions = this.calculateDimensions();
    }

    extractConfig(centerConfig) {
        return {
            fontStyle: centerConfig.fontStyle || 'Arial',
            text: centerConfig.text,
            color: centerConfig.color || '#000',
            maxFontSize: centerConfig.maxFontSize || 75,
            minFontSize: centerConfig.minFontSize,
            sidePadding: centerConfig.sidePadding || 20,
            lineHeight: centerConfig.lineHeight || 25,
            wrapText: false
        };
    }

    calculateDimensions() {
        const innerRadius = this.chart.innerRadius;
        const sidePaddingCalculated = (this.config.sidePadding / 100) * (innerRadius * 2);
        
        return {
            elementWidth: (innerRadius * 2) - sidePaddingCalculated,
            elementHeight: innerRadius * 2,
            centerX: (this.chart.chartArea.left + this.chart.chartArea.right) / 2,
            centerY: (this.chart.chartArea.top + this.chart.chartArea.bottom) / 2
        };
    }

    calculateOptimalFontSize() {
        const baseFontSize = 30;
        this.ctx.font = `${baseFontSize}px ${this.config.fontStyle}`;
        
        const stringWidth = this.ctx.measureText(this.config.text).width;
        const widthRatio = this.dimensions.elementWidth / stringWidth;
        const newFontSize = Math.floor(baseFontSize * widthRatio);
        
        return Math.min(newFontSize, this.dimensions.elementHeight, this.config.maxFontSize);
    }

    shouldRender(fontSize) {
        return this.config.minFontSize === undefined || fontSize > this.config.minFontSize;
    }

    setupTextStyles(fontSize) {
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.font = `${fontSize}px ${this.config.fontStyle}`;
        this.ctx.fillStyle = this.config.color;
    }

    renderSimpleText() {
        this.ctx.fillText(this.config.text, this.dimensions.centerX, this.dimensions.centerY);
    }

    renderWrappedText() {
        const lines = this.wrapText();
        const startY = this.dimensions.centerY - (lines.length / 2) * this.config.lineHeight;
        
        lines.forEach((line, index) => {
            const y = startY + (index * this.config.lineHeight);
            this.ctx.fillText(line, this.dimensions.centerX, y);
        });
    }

    wrapText() {
        const words = this.config.text.split(' ');
        const lines = [];
        let currentLine = '';
        
        for (let i = 0; i < words.length; i++) {
            const testLine = currentLine + words[i] + ' ';
            const testWidth = this.ctx.measureText(testLine).width;
            
            if (testWidth > this.dimensions.elementWidth && i > 0) {
                lines.push(currentLine.trim());
                currentLine = words[i] + ' ';
            } else {
                currentLine = testLine;
            }
        }
        
        lines.push(currentLine.trim());
        return lines;
    }

    render() {
        const fontSize = this.calculateOptimalFontSize();
        
        if (!this.shouldRender(fontSize)) return;
        
        this.setupTextStyles(fontSize);
        
        if (!this.config.wrapText) {
            this.renderSimpleText();
        } else {
            this.renderWrappedText();
        }
    }
}
export default {
    name: "categories",
    components: {
        PieChart
    },
    data() {
        return {
            resultsChartHappiness: [],
            labelsChart: [],
            dataChart: [],
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                legend: {
                    display: true,
                    position: 'right',
                    labels: {
                        boxWidth: 15,
                        padding: 20,
                        fontColor: '#333',
                        fontSize: 12
                    }
                },
                tooltips: {
                    enabled: true,
                    mode: 'single',
                    callbacks: {
                        label: function(tooltipItem, data) {
                            const dataset = data.datasets[tooltipItem.datasetIndex]
                            const label = data.labels[tooltipItem.index]
                            const value = dataset.data[tooltipItem.index]
                            const total = dataset.data.reduce((a, b) => a + b, 0)
                            const percentage = ((value / total) * 100).toFixed(1)
                            return `${label}: ${value} (${percentage}%)`
                        }
                    }
                },
                animation: {
                    animateRotate: true,
                    animateScale: false,
                    duration: 1000,
                    easing: 'easeOutQuart'
                },
                plugins: {
                    centerText: centerTextPlugin
                }
            },
        }
    },
    
    async mounted() {
        await this.loadChartData()
    },
    
    methods: {
        formatTooltipLabel(tooltipItem, data) {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            const label = data.labels[tooltipItem.index];
            const value = dataset.data[tooltipItem.index];
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
        },
        async loadChartData() {
            
            try {
                // Obtener datos del servicio
                this.resultsChartHappiness = await this.actionGetResultsHappiness()
                
                // Limpiar arrays anteriores
                this.labelsChart = []
                this.dataChart = []
                
                // Procesar los datos obtenidos
                if (this.resultsChartHappiness && this.resultsChartHappiness.length > 0) {
                    this.resultsChartHappiness.forEach((res) => {
                        this.labelsChart.push(res.name)
                        this.dataChart.push(res.count)
                    })
                } else {
                    console.warn("No se encontraron datos de felicidad")
                }
                
            } catch (err) {
                console.error("Error cargando datos:", err)
            }
        },

        // Generar colores aleatorios
        generateRandomColors(count, opacity = 1) {
            const colors = []
            for (let i = 0; i < count; i++) {
                const r = Math.floor(Math.random() * 256)
                const g = Math.floor(Math.random() * 256)
                const b = Math.floor(Math.random() * 256)
                
                if (opacity === 1) {
                    colors.push(`rgb(${r}, ${g}, ${b})`)
                } else {
                    colors.push(`rgba(${r}, ${g}, ${b}, ${opacity})`)
                }
            }
            return colors
        },

        // Generar colores con mayor vibración
        generateVibrantColors(count, opacity = 1) {
            const colors = []
            const hueStep = 360 / count
            
            for (let i = 0; i < count; i++) {
                const hue = Math.floor(i * hueStep + Math.random() * 60)
                const saturation = Math.floor(Math.random() * 30) + 70 // 70-100%
                const lightness = Math.floor(Math.random() * 40) + 50  // 50-70%
                
                if (opacity === 1) {
                    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
                } else {
                    colors.push(`hsla(${hue}, ${saturation}%, ${lightness}%, ${opacity})`)
                }
            }
            return colors
        },

        ...mapActions({
            actionGetResultsHappiness: `${HAPPINESS_M}${ACTION_GET_RESULTS_HAPPINESS}`,
        }),
    },
    
    computed: {
        // Datos de la gráfica calculados dinámicamente
        chartData() {
            if (!this.hasData) {
                return {
                    labels: [],
                    datasets: []
                }
            }
            
            const count = this.labelsChart.length
            const colors = this.generateVibrantColors(count)
            const hoverColors = this.generateVibrantColors(count, 0.8)
            
            return {
                labels: this.labelsChart,
                datasets: [{
                    data: this.dataChart,
                    backgroundColor: colors,
                    hoverBackgroundColor: hoverColors
                }]
            }
        },
        
        // Verificar si hay datos para mostrar
        hasData() {
            return this.labelsChart.length > 0 && this.dataChart.length > 0
        },
        
        // Total de respuestas
        totalResponses() {
            return this.dataChart.reduce((total, count) => total + count, 0)
        },
        
        ...mapGetters({
            resultsHappiness: `${HAPPINESS_M}${GET_RESULTS_HAPPINESS}`,
        }),
    },
};
</script>
  
<style lang="css" scoped>
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-family: 'Amsi Pro', sans-serif;
        color: #2D0049;
        font-weight: normal;
    }

    .chart-container {
        margin: 0 auto;
        font-family: 'Arial', sans-serif;
    }

    .chart-wrapper {
        position: relative;
        padding: 20px;
        text-align: center;
    }

    .no-data {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
        color: #757575;
        background-color: #f5f5f5;
        border-radius: 8px;
        margin: 20px 0;
    }

    .data-info {
        margin-top: 15px;
        font-size: 14px;
        color: #666;
        font-weight: 500;
        text-align: center;
    }

    .custom-legend {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 15px;
        margin-top: 20px;
    }

    .legend-item {
        display: flex;
        align-items: center;
        background: #f8f9fa;
        padding: 8px 12px;
        border-radius: 5px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }

    .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-right: 8px;
    }

    .legend-label {
        font-size: 12px;
        color: #333;
        font-weight: 500;
    }

    /* Responsive */
    @media (max-width: 600px) {        
        .custom-legend {
            flex-direction: column;
            align-items: center;
        }
        
        .controls button {
            display: block;
            width: 100%;
            margin: 5px 0;
        }
    }

    @media screen and (max-width: 900px) {
        h1 {
            font-size: 3vh;
        }

        h2 {
            font-size: 2vh;
        }
    }

    @media screen and (max-width: 500px) {
        h1 {
            font-size: 3vh;
        }

        h2 {
            font-size: 2vh;
        }

        .headerPizarra{
            display: block;
        }

        .containerPizarra{
            gap: 2vh;
            padding: 2vh
        }
    }
</style>